import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, Star } from "lucide-react";
import Khushi from "../../images/KhushiGupta.png";
import Pratham from "../../images/PrathamMalik.jpg";
import Kavya from "../../images/KavyaShukla.jpg";
import Rasika from "../../images/RASIKABANSOD.jpg";
import Gurpreet from "../../images/Gurpreet.jpg";
import Saurabh from "../../images/SaurabhVedwal.jpg";
import Shantanu from "../../images/ShantanuDeshpande.jpg";
import FadeIn from "../ui/FadeIn";

const testimonials = [
  {
    quote: "Interacting with people and getting to know their best experiences was very therapeutic. I realized that kindness hasn't vanished from the world.",
    name: "Khushi Gupta",
    role: "Volunteer",
    image: Khushi,
    rating: 5,
  },
  {
    quote: "My SPOC was Aditya sir. He was really cooperative & he was quick in solving our queries. He went an extra mile for all of us by organising short meetings every week which were really fun. Thank You Aditya sir for such a wonderful experience. Grateful for you :)",
    name: "Pratham Malik",
    role: "Volunteer",
    image: Pratham,
    rating: 5,
  },
  {
    quote: "I liked how I personally got to know different kinds of kindness acts people do. It opened my eyes to the simple ways we can all make a difference each day.",
    name: "Kavya Shukla",
    role: "Volunteer",
    image: Kavya,
    rating: 5,
  },
  {
    quote: "It's a great way to give back to your local community and the world or make a difference to the people around them. One of the best things about volunteering is meeting the other volunteers and our SPOC.",
    name: "Rasika Bansod",
    role: "Volunteer, 1MillionStory Campaign",
    image: Rasika,
    rating: 5,
  },
  {
    quote: "Volunteering at KU helped me realise that perhaps kindness isn't rare; but in fact, is everywhere. The need of the hour is to be observant & kind. Today's youth must come forward to join KU in this beautiful movement- the kindness movement.",
    name: "Gurpreet Kaur N",
    role: "Volunteer, 1MillionStory Campaign",
    image: Gurpreet,
    rating: 5,
  },
  {
    quote: "Connecting with people and collecting their stories. I enjoyed listening their stories and sharing my own observations.",
    name: "Saurabh Kumar",
    role: "Volunteer, 1MillionStory Campaign",
    image: Saurabh,
    rating: 5,
  },
  {
    quote: "I was a part of the 'Kindness Act Collecting' internship by KU. The weekly workshops were so enriching. Also, team KU & my co-interns were extremely supportive & great to work with! My faith in kindness has increased manifolds after joining KU & I look forward to working with KU soon again.",
    name: "Khushi",
    role: "Volunteer, 1MillionStory Campaign",
    image: Khushi,
    rating: 5,
  },
  {
    quote: "Ku gave me the platform to influence people to BE kind. It also helped me gain a new perspective towards being kind. I've realised the power of kindness & that kindness perhaps is a strength & not a weakness! The satisfaction of having played a small part in this journey of kindness gives me immense joy & fills me with gratitude.",
    name: "Shantanu Deshpande",
    role: "Volunteer, 1MillionStory Campaign",
    image: Shantanu,
    rating: 5,
  },
];

const TestimonialsPage = () => {
  const [featured, setFeatured] = useState(0);
  const t = testimonials[featured];

  // Auto-rotate testimonials every 6 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setFeatured((i) => (i + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-ku-cream to-white pt-0">
      {/* Hero Header */}
      <section className="relative py-10 md:py-14 bg-ku-gradient overflow-hidden">
        <div className="absolute inset-0 bg-noise opacity-20" aria-hidden />
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/5 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-ku-gold/10 blur-3xl" />
        <div className="ku-container relative text-center">
          <FadeIn>
            <span className="ku-eyebrow text-ku-lavender text-sm">Voices of Kindness</span>
            <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white mt-2 mb-2 leading-tight">
              Real Stories
            </h1>
            <p className="font-body text-white/80 text-base max-w-2xl mx-auto">
              Discover the transformative impact of kindness through the voices of our community.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Featured Testimonial - Creative Carousel */}
      <section className="py-12 md:py-16 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 right-1/4 w-72 h-72 bg-ku-purple/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/3 left-1/4 w-80 h-80 bg-ku-gold/5 rounded-full blur-3xl" />
        </div>

        <div className="ku-container relative max-w-2xl">
          <FadeIn>
            <AnimatePresence mode="wait">
              <motion.div
                key={featured}
                initial={{ opacity: 0, scale: 0.95, rotateY: -10 }}
                animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                exit={{ opacity: 0, scale: 0.95, rotateY: 10 }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
                className="relative"
              >
                {/* Enhanced Card */}
                <div className="relative rounded-3xl bg-white shadow-2xl overflow-hidden">
                  {/* Gradient top accent */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-ku-purple via-ku-gold to-ku-violet" />

                  <div className="p-6 md:p-10 text-center">
                    {/* Animated quote icon */}
                    {/* Animated quote icon */}
                    <motion.div
                      initial={{ opacity: 0, y: -15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2, duration: 0.4 }}
                      className="mb-4"
                    >
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-ku-purple to-ku-violet flex items-center justify-center mx-auto shadow-lg">
                        <Quote className="w-6 h-6 text-white" />
                      </div>
                    </motion.div>

                    {/* Stars with animation */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3, duration: 0.4 }}
                      className="flex justify-center gap-1.5 mb-4"
                    >
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.4 + i * 0.08 }}
                        >
                          <Star className="w-4 h-4 text-amber-400" fill="currentColor" />
                        </motion.div>
                      ))}
                    </motion.div>

                    {/* Quote text with better styling */}
                    <motion.p
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4, duration: 0.4 }}
                      className="font-body text-gray-700 text-base md:text-lg leading-relaxed italic mb-6 font-light"
                    >
                      &ldquo;{t.quote}&rdquo;
                    </motion.p>

                    {/* Author info with enhanced styling */}
                    <motion.div
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5, duration: 0.4 }}
                      className="border-t border-ku-soft/30 pt-5"
                    >
                      <div className="flex flex-col items-center gap-3">
                        {t.image ? (
                          <img
                            src={t.image}
                            alt={t.name}
                            className="w-14 h-14 rounded-full object-cover ring-3 ring-ku-purple shadow-md"
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-ku-purple to-ku-violet flex items-center justify-center ring-3 ring-white shadow-md">
                            <span className="font-display font-bold text-white text-lg">{t.name[0]}</span>
                          </div>
                        )}
                        <div>
                          <p className="font-display font-bold text-ku-royal text-sm md:text-base">{t.name}</p>
                          <p className="font-accent text-ku-gold font-semibold text-xs tracking-wide">{t.role}</p>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Controls */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.4 }}
              className="flex items-center justify-center gap-4 mt-6"
            >
              {/* <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                type="button"
                onClick={() => setFeatured((i) => (i - 1 + testimonials.length) % testimonials.length)}
                className="w-9 h-9 rounded-full bg-ku-purple/20 border-2 border-ku-purple/30 flex items-center justify-center text-ku-royal hover:bg-ku-purple hover:text-white transition-all"
                aria-label="Previous"
              >
                <ChevronLeft className="w-4 h-4" />
              </motion.button> */}

              <div className="flex gap-2">
                {testimonials.map((_, i) => (
                  <motion.button
                    key={i}
                    type="button"
                    onClick={() => setFeatured(i)}
                    whileHover={{ scale: 1.15 }}
                    aria-label={`Testimonial ${i + 1}`}
                    className={`rounded-full transition-all ${i === featured ? "w-6 h-2.5 bg-gradient-to-r from-ku-purple to-ku-violet" : "w-2.5 h-2.5 bg-ku-soft hover:bg-ku-lavender"}`}
                  />
                ))}
              </div>

              {/* <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                type="button"
                onClick={() => setFeatured((i) => (i + 1) % testimonials.length)}
                className="w-9 h-9 rounded-full bg-gradient-to-br from-ku-purple to-ku-violet flex items-center justify-center text-white hover:shadow-lg transition-all"
                aria-label="Next"
              >
                <ChevronRight className="w-4 h-4" />
              </motion.button> */}
            </motion.div>

            {/* Counter info */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.4 }}
              className="text-center mt-4"
            >
              <p className="font-body text-gray-600 text-xs">
                <span className="font-bold text-ku-royal">{featured + 1}</span> of <span className="font-bold text-ku-royal">{testimonials.length}</span>
              </p>
            </motion.div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default TestimonialsPage;
